// @flow
import { NotificationMessage, toastSuccess, toastError } from 'bench-internal-theme';
import { ClientIdContext } from 'bk-web-app';
import React from 'react';
import { Mutation, Query } from '@apollo/client/react/components';
// $FlowFixMe - TS file
import env from 'bench-environment';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../features';

import { Spinner } from 'spinner';

import FinancialPackageNotesHeader from './FinancialPackageNotesHeader';
import {
  CLIENT_YEARLY_DATA,
  CREATE_CLIENT_YEARLY_DATA,
  UPDATE_CLIENT_YEARLY_DATA,
} from './queries';

type FinancialPackageNotesHeaderContainerProps = {
  endDate: string,
  fiscalYear: number,
  hashtagFiscalYearTag: string,
  startDate: string,
};

const onStatusChangeRequested =
  (clientId, fiscalYear, yearlyData, createYearlyData, updateYearlyData) => (category, status) => {
    const requestedStatusChange = { year: fiscalYear, [category]: status };
    const persistedYearData = yearlyData.find(x => x.year === fiscalYear);
    // Combine persisted data (if available) with requested change and remove __typename
    const { __typename, ...updatedYearlyData } = {
      ...persistedYearData,
      ...requestedStatusChange,
    };
    const isYearDataPersisted = !!persistedYearData;
    const createOrUpdateYearlyData = isYearDataPersisted ? updateYearlyData : createYearlyData;
    createOrUpdateYearlyData({
      variables: { clientId, yearlyData: updatedYearlyData },
    });
  };

const uploadFinancialPackageNotesToFolders = (clientId, startDate, endDate, uploadToTaxFolder) => {
  fetch(`${env.location.apiRoot}/reports/api/yef/xls/${clientId}/${startDate}/${endDate}`, {
    credentials: 'include',
  }).then(response => {
    if (response.ok) {
      const folderName = uploadToTaxFolder ? 'Tax' : 'Internal';
      toastSuccess('Upload successful!', `YEF package added to the ${folderName} folder.`);
    } else {
      toastError('Unable to upload to folder.', `Please try again.`);
    }
  });
};

const FinancialPackageNotesHeaderContainer = ({
  endDate,
  fiscalYear,
  hashtagFiscalYearTag,
  startDate,
}: FinancialPackageNotesHeaderContainerProps) => {
  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;
  const showNewYefStatuses =
    features[SplitFeatureFlags.APPROVE_YEF_BACKEND]?.value === FeatureValue.FEATURE_ON;

  return (
    <ClientIdContext.Consumer>
      {clientId => (
        <Query query={CLIENT_YEARLY_DATA} variables={{ clientId }}>
          {(getYearlyData: any) => {
            if (getYearlyData.error) {
              return (
                <NotificationMessage styleType="error">
                  There was an issue loading the annual tasks.
                </NotificationMessage>
              );
            }
            if (getYearlyData.loading) {
              return <Spinner />;
            }

            const { yearlyData, clientDetails } = getYearlyData.data.client;
            let selectedFiscalYearData = yearlyData.find(
              selectedYear => selectedYear.year === fiscalYear,
            );

            if (!selectedFiscalYearData) {
              selectedFiscalYearData = {
                cpaAdjustment: null,
                equityClosingAdjustment: null,
                trialBalanceOrPriorReturn: null,
                year: fiscalYear,
                yearEndReview: null,
                yearEndFinancialPackage: null,
              };
            }

            const mutationRefetchQueries = [{ query: CLIENT_YEARLY_DATA, variables: { clientId } }];
            const { yearEndReview, yearEndFinancialPackage } = selectedFiscalYearData;
            const yefStatusesForPackageCreation = showNewYefStatuses
              ? ['COMPLETE', 'CLIENT_REVIEWING', 'CLIENT_REQUESTED_ADJUSTMENT']
              : ['COMPLETE'];
            const areYearEndPackagesComplete =
              yearEndReview === 'COMPLETE' &&
              yefStatusesForPackageCreation.includes(yearEndFinancialPackage);

            return (
              <Mutation
                mutation={CREATE_CLIENT_YEARLY_DATA}
                refetchQueries={mutationRefetchQueries}
              >
                {createYearlyData => (
                  <Mutation
                    mutation={UPDATE_CLIENT_YEARLY_DATA}
                    refetchQueries={mutationRefetchQueries}
                  >
                    {updateYearlyData => (
                      <FinancialPackageNotesHeader
                        areYearEndPackagesComplete={areYearEndPackagesComplete}
                        clientDetails={clientDetails}
                        fiscalYear={fiscalYear}
                        hashtagFiscalYearTag={hashtagFiscalYearTag}
                        onStatusChange={onStatusChangeRequested(
                          clientId,
                          fiscalYear,
                          yearlyData,
                          createYearlyData,
                          updateYearlyData,
                        )}
                        showNewYefStatuses={showNewYefStatuses}
                        uploadFinancialPackageNotes={() =>
                          uploadFinancialPackageNotesToFolders(
                            clientId,
                            startDate,
                            endDate,
                            showNewYefStatuses,
                          )
                        }
                        yearlyData={selectedFiscalYearData}
                      />
                    )}
                  </Mutation>
                )}
              </Mutation>
            );
          }}
        </Query>
      )}
    </ClientIdContext.Consumer>
  );
};

export default FinancialPackageNotesHeaderContainer;
